import { FC, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useContractManagement } from 'src/apis/contract-management/use-contract-management';
import { useOperatorByTso } from 'src/apis/contract-management/use-operator-by-tso';
import { useTsoPlatformIds } from 'src/apis/monolith/use-operator-platform-ids';
import { useOrganisationAssignments } from 'src/apis/monolith/use-organisation-assignments';
import { useUserAssignment } from 'src/apis/monolith/use-user-assignment';
import { PageSpinner } from 'src/components/spinner-container';
import { isNetworkError } from 'src/hooks/use-axios';
import { useTso } from 'src/pages/market-information/players/operators-detail/use-tso';
import { useTsoAssignmentDocuments } from 'src/pages/market-information/players/operators-detail/use-tso-assignment-documents';
import { Page } from 'src/pages/operators/assignments/details/page';
import { useGetOldUserIdDeprecated } from 'src/pages/settings/organisation/users/details/use-get-old-user-id';
import { useShipperUser } from 'src/pages/shipper-admin/details/use-shipper-user';
import {
  useShipperRegistration,
  useShipperRegistrationPublic,
} from 'src/pages/shippers/useShipperRegistration';

type Props = {
  oldTsoId: string;
  userId: string;
};

const AssignmentsDetailsWithOldId: FC<Props> = ({ oldTsoId, userId }) => {
  const tso = useTso(oldTsoId);
  const contractManagement = useContractManagement();
  const shipperRegistrationPublic = useShipperRegistrationPublic();
  const assignmentDocuments = useTsoAssignmentDocuments({
    tsoId: oldTsoId,
    shipperRegistrationPublic,
  });
  const shipperRegistration = useShipperRegistration();
  const organisationAssignments = useOrganisationAssignments();
  const userAssignment = useUserAssignment({
    tsoId: oldTsoId,
    userId,
  });
  const shipperUser = useShipperUser(userId);
  const caMaOperator = useOperatorByTso({ tso });

  if (
    !tso.response ||
    !shipperUser.response ||
    !shipperRegistrationPublic.response ||
    !shipperRegistration.response ||
    !organisationAssignments.response ||
    !userAssignment.response ||
    !assignmentDocuments.response ||
    (caMaOperator && !caMaOperator.loaded) ||
    (!contractManagement.response && !isNetworkError(contractManagement.error))
  ) {
    return <PageSpinner />;
  }

  return (
    <Page
      tsoId={oldTsoId}
      userId={userId}
      tso={tso}
      shipperUser={shipperUser}
      contractManagement={contractManagement}
      shipperRegistrationPublic={shipperRegistrationPublic}
      shipperRegistration={shipperRegistration}
      organisationAssignments={organisationAssignments}
      userAssignment={userAssignment}
      assignmentDocuments={assignmentDocuments}
      caMaOperator={caMaOperator}
    />
  );
};

export const AssigmentDetails = () => {
  const { operatorUuid, userUuid } = useParams<{
    operatorUuid: string;
    userUuid: string;
  }>();

  const tsoPlatformIds = useTsoPlatformIds(operatorUuid);
  const getOldUserId = useGetOldUserIdDeprecated(userUuid);

  useEffect(() => {
    getOldUserId.execute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!getOldUserId.response || !tsoPlatformIds.response)
    return <PageSpinner />;

  return (
    <AssignmentsDetailsWithOldId
      oldTsoId={tsoPlatformIds.response.data.referenceId.toString()}
      userId={getOldUserId.response.data.customerUserId.toString()}
    />
  );
};
